import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "src/layout/admin";
import GenericDataLayout from "src/components/custom/GenericDataLayout";
import GenericTable from "src/components/custom/GenericTable";
import Modal from "src/components/modal";
import SearchableSelect from "src/components/custom/SearchableSelect";
import Button from "src/components/custom/Button";
import CustomInput from "src/components/custom/CustomInput";
import { useForm } from "react-hook-form";
import {
  getMethodApiCall,
  postMethodApiCall,
  putMethodApiCall,
} from "src/services/api";
import { toast } from "react-toastify";

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  industryname: "",
  latitude: "",
  longitude: "",
  district: "",
  iti_id: "",
  trade_id: "",
};

const MODAL_MODE = {
  ADD: "add",
  EDIT: "edit",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Only include a param if it has a real, non-"all" value */
const buildParams = (raw) =>
  Object.fromEntries(
    Object.entries(raw).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && v !== "all"
    )
  );

// ─── Component ────────────────────────────────────────────────────────────────

const Industry = () => {
  // ── Table state ────────────────────────────────────────────────────────────
  const [tableRows, setTableRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
  });

  // ── Filter state ───────────────────────────────────────────────────────────
  const [filterDistrict, setFilterDistrict] = useState("all");
  const [filterIti, setFilterIti] = useState("all");
  const [filterTrade, setFilterTrade] = useState("all");
  const [filterIndustry, setFilterIndustry] = useState("all");

  const [filterItiOptions, setFilterItiOptions] = useState([]);
  const [filterTradeOptions, setFilterTradeOptions] = useState([]);
  const [filterIndustryOptions, setFilterIndustryOptions] = useState([]);

  const [filterDisabled, setFilterDisabled] = useState({
    iti: true,
    trade: true,
    industry: true,
  });

  // ── Modal state ────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(MODAL_MODE.ADD);
  const [selectedRow, setSelectedRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Dropdown options ───────────────────────────────────────────────────────
  const [districtOptions, setDistrictOptions] = useState([]);
  const [itiOptions, setItiOptions] = useState([]);
  const [tradeOptions, setTradeOptions] = useState([]);

  const [modalDisabled, setModalDisabled] = useState({
    iti: true,
    trade: true,
  });

  // ── Form ───────────────────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY_FORM });

  // ─── API helpers ────────────────────────────────────────────────────────────

  const fetchDistricts = async () => {
    try {
      const res = await getMethodApiCall("/v1/api/trainee/get_district_picklist");
      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((d) => d?.district)
          .map((d) => ({ label: d.district, value: d.district }));
        setDistrictOptions(options);
      }
    } catch (err) {
      console.error("Districts fetch error", err);
    }
  };

  const fetchItisByDistrict = async (district) => {
    if (!district || district === "all") return [];
    try {
      const res = await getMethodApiCall(
        `/v1/api/trainee/get_iti_picklist/${encodeURIComponent(district)}`
      );
      return res?.data?.success ? res.data.data || [] : [];
    } catch {
      return [];
    }
  };

  const fetchTradesByIti = async (iti_id) => {
    if (!iti_id || iti_id === "all") return [];
    try {
      const res = await getMethodApiCall(
        `/v1/api/trainee/trades_by_iti/${iti_id}`
      );
      return res?.data?.success ? res.data.data || [] : [];
    } catch {
      return [];
    }
  };

  const fetchIndustriesByTradeAndIti = async (trade_id, iti_id) => {
    if (!trade_id || !iti_id || trade_id === "all" || iti_id === "all") return [];
    try {
      const res = await getMethodApiCall(
        `/v1/api/trainee/industries_by_trade_and_iti/${encodeURIComponent(trade_id)}/${encodeURIComponent(iti_id)}`
      );
      return res?.data?.success ? res.data.data || [] : [];
    } catch {
      return [];
    }
  };

  // ─── List fetch ─────────────────────────────────────────────────────────────

  const fetchList = useCallback(
    async (page = 1, limit = pagination.limit, overrides = {}) => {
      setListLoading(true);
      try {
        const rawParams = {
          page,
          limit,
          iti_id:
            overrides.iti !== undefined ? overrides.iti : filterIti,
          trade_id:
            overrides.trade !== undefined ? overrides.trade : filterTrade,
          industry_id:
            overrides.industry !== undefined ? overrides.industry : filterIndustry,
        };

        const params = buildParams(rawParams);
        // Always include page and limit
        params.page = page;
        params.limit = limit;

        const res = await getMethodApiCall(
          "/v1/api/mcc/iti-trade-industry",
          params
        );
        if (res?.status === 200 && res?.data?.success) {
          setTableRows(res.data.data || []);
          const p = res.data.pagination || {};
          setPagination((prev) => ({
            ...prev,
            page: p.page ?? page,
            limit: p.limit ?? limit,
            totalRecords: p.total ?? 0,
          }));
        } else {
          setTableRows([]);
        }
      } catch {
        setTableRows([]);
        toast.error("Failed to load industries", 1000);
      } finally {
        setListLoading(false);
      }
    },
    [filterIti, filterTrade, filterIndustry, pagination.limit]
  );

  useEffect(() => {
    fetchDistricts();
    fetchList(1, 10, { iti: "all", trade: "all", industry: "all" });
  }, []);

  // ─── Filter handlers ────────────────────────────────────────────────────────

  const handleFilterDistrictChange = async (val) => {
    setFilterDistrict(val);
    setFilterIti("all");
    setFilterTrade("all");
    setFilterIndustry("all");
    setFilterTradeOptions([]);
    setFilterIndustryOptions([]);

    if (val && val !== "all") {
      const list = await fetchItisByDistrict(val);
      setFilterItiOptions(list.map((i) => ({ label: i.iti_name, value: i.id })));
      setFilterDisabled({ iti: false, trade: true, industry: true });
    } else {
      setFilterItiOptions([]);
      setFilterDisabled({ iti: true, trade: true, industry: true });
    }
  };

  const handleFilterItiChange = async (val) => {
    setFilterIti(val);
    setFilterTrade("all");
    setFilterIndustry("all");
    setFilterIndustryOptions([]);

    if (val && val !== "all") {
      const list = await fetchTradesByIti(val);
      setFilterTradeOptions(list.map((t) => ({ label: t.trade_name, value: t.id })));
      setFilterDisabled((prev) => ({ ...prev, trade: false, industry: true }));
    } else {
      setFilterTradeOptions([]);
      setFilterDisabled((prev) => ({ ...prev, trade: true, industry: true }));
    }

    fetchList(1, pagination.limit, { iti: val, trade: "all", industry: "all" });
  };

  const handleFilterTradeChange = async (val) => {
    setFilterTrade(val);
    setFilterIndustry("all");

    if (val && val !== "all") {
      const list = await fetchIndustriesByTradeAndIti(val, filterIti);
      setFilterIndustryOptions(list.map((i) => ({ label: i.industry_name, value: i.id })));
      setFilterDisabled((prev) => ({ ...prev, industry: false }));
    } else {
      setFilterIndustryOptions([]);
      setFilterDisabled((prev) => ({ ...prev, industry: true }));
    }

    fetchList(1, pagination.limit, { trade: val, industry: "all" });
  };

  const handleFilterIndustryChange = (val) => {
    setFilterIndustry(val);
    fetchList(1, pagination.limit, { industry: val });
  };

  const onResetFilters = () => {
    setFilterDistrict("all");
    setFilterIti("all");
    setFilterTrade("all");
    setFilterIndustry("all");
    setFilterItiOptions([]);
    setFilterTradeOptions([]);
    setFilterIndustryOptions([]);
    setFilterDisabled({ iti: true, trade: true, industry: true });
    fetchList(1, pagination.limit, { iti: "all", trade: "all", industry: "all" });
  };

  // ─── Modal cascade handlers (via react-hook-form setValue + watch) ──────────

  const handleDistrictChange = async (district) => {
    setValue("district", district);
    setValue("iti_id", "");
    setValue("trade_id", "");
    setItiOptions([]);
    setTradeOptions([]);
    setModalDisabled({ iti: true, trade: true });

    if (!district) return;
    const list = await fetchItisByDistrict(district);
    setItiOptions(list.map((i) => ({ label: i.iti_name, value: i.id })));
    setModalDisabled((prev) => ({ ...prev, iti: false }));
  };

  const handleItiChange = async (iti_id) => {
    setValue("iti_id", iti_id);
    setValue("trade_id", "");
    setTradeOptions([]);
    setModalDisabled((prev) => ({ ...prev, trade: true }));

    if (!iti_id) return;
    const list = await fetchTradesByIti(iti_id);
    setTradeOptions(list.map((t) => ({ label: t.trade_name, value: t.id })));
    setModalDisabled((prev) => ({ ...prev, trade: false }));
  };

  const handleTradeChange = (trade_id) => {
    setValue("trade_id", trade_id);
  };

  // ─── Modal open / close ─────────────────────────────────────────────────────

  const openModal = async (mode, row = null) => {
    setModalMode(mode);
    setSelectedRow(row);

    if (mode === MODAL_MODE.ADD) {
      reset(EMPTY_FORM);
      setItiOptions([]);
      setTradeOptions([]);
      setModalDisabled({ iti: true, trade: true });
    } else if (row) {
      reset({
        industryname: row.industryname ?? "",
        latitude: row.latitude ?? "",
        longitude: row.longitude ?? "",
        district: row.district ?? "",
        iti_id: row.iti_id ?? "",
        trade_id: row.trade_id ?? "",
      });

      const itis = await fetchItisByDistrict(row.district);
      setItiOptions(itis.map((i) => ({ label: i.iti_name, value: i.id })));

      const trades = await fetchTradesByIti(row.iti_id);
      setTradeOptions(trades.map((t) => ({ label: t.trade_name, value: t.id })));

      setModalDisabled({ iti: false, trade: false });
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  // ─── Form submit ────────────────────────────────────────────────────────────

  const onSubmit = async (data) => {
    if (!data.district) return toast.error("Please select a District", 1000);
    if (!data.iti_id)   return toast.error("Please select an ITI", 1000);
    if (!data.trade_id) return toast.error("Please select a Trade", 1000);

    setSubmitting(true);

    const payload = {
      industryname: data.industryname.trim(),
      latitude: Number(data.latitude),
      longitude: Number(data.longitude),
      district: data.district,
      iti_id: Number(data.iti_id),
      trade_id: Number(data.trade_id),
      delete_previous_mapping: modalMode === MODAL_MODE.EDIT ? {trade_id: Number(selectedRow.trade_id), iti_id: Number(selectedRow.iti_id)} : undefined,
    };

    try {
      let res;
      if (modalMode === MODAL_MODE.ADD) {
        res = await postMethodApiCall("/v1/api/mcc/industries", payload);
      } else {
        res = await putMethodApiCall(
          `/v1/api/mcc/industries/${selectedRow.industry_id}`,
          payload
        );
      }

      if (res?.data?.success) {
        toast.success(res.data.message || "Operation successful", 1000);
        closeModal();
        fetchList();
      } else {
        toast.error(res?.data?.message || "Operation failed", 1000);
      }
    } catch {
      toast.error("An error occurred. Please try again.", 1000);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Watched values for SearchableSelect controlled binding ─────────────────
  const watchedDistrict = watch("district");
  const watchedIti = watch("iti_id");
  const watchedTrade = watch("trade_id");

  // ─── Table config ───────────────────────────────────────────────────────────

  const filters = [
    {
      label: "District",
      type: "select",
      value: filterDistrict,
      options: districtOptions,
      onChange: handleFilterDistrictChange,
    },
    {
      label: "ITI",
      type: "select",
      value: filterIti,
      options: filterItiOptions,
      onChange: handleFilterItiChange,
      disabled: filterDisabled.iti,
    },
    {
      label: "Trade",
      type: "select",
      value: filterTrade,
      options: filterTradeOptions,
      onChange: handleFilterTradeChange,
      disabled: filterDisabled.trade,
    },
    {
      label: "Industry",
      type: "select",
      value: filterIndustry,
      options: filterIndustryOptions,
      onChange: handleFilterIndustryChange,
      disabled: filterDisabled.industry,
    },
  ];

  const tableColumns = [
    { header: "INDUSTRY NAME", accessor: "industryname" },
    { header: "LATITUDE",      accessor: "latitude" },
    { header: "LONGITUDE",     accessor: "longitude" },
    { header: "ITI",           accessor: "itiname" },
    { header: "TRADE",         accessor: "tradename" },
    {
      header: "ACTIONS",
      accessor: "actions",
      render: (row) => (
        <button
          onClick={() => openModal(MODAL_MODE.EDIT, row)}
          className="text-xs px-3 py-1 rounded border border-primary text-primary hover:bg-primary/10 font-medium transition"
        >
          Edit
        </button>
      ),
    },
  ];

  // ─── Form fields ────────────────────────────────────────────────────────────

  const renderFormFields = () => (
    <div className="space-y-5">
      {/* Industry Name */}
      <CustomInput
        label="Industry Name"
        name="industryname"
        register={register}
        validation={{ required: "Industry name is required" }}
        error={errors.industryname}
      />

      {/* Latitude & Longitude */}
      <div className="grid grid-cols-2 gap-4">
        <CustomInput
          label="Latitude"
          name="latitude"
          type="number"
          register={register}
          validation={{
            required: "Latitude is required",
            valueAsNumber: true,
            min: { value: -90, message: "Latitude must be ≥ -90" },
            max: { value: 90, message: "Latitude must be ≤ 90" },
            // validate: (value) =>
            //   /^\-?\d+(\.\d{1,6})?$/.test(value) || "Max 6 decimal places allowed",
          }}
          error={errors.latitude}
        />
        <CustomInput
          label="Longitude"
          name="longitude"
          type="number"
          register={register}
          validation={{
            required: "Longitude is required",
            valueAsNumber: true,
            min: { value: -180, message: "Longitude must be ≥ -180" },
            max: { value: 180, message: "Longitude must be ≤ 180" },
            // validate: (value) =>
            //   /^\-?\d+(\.\d{1,6})?$/.test(value) || "Max 6 decimal places allowed",
          }}
          error={errors.longitude}
        />
      </div>

      {/* District */}
      <SearchableSelect
        label="District *"
        options={districtOptions}
        value={watchedDistrict}
        onChange={handleDistrictChange}
      />

      {/* ITI & Trade */}
      <div className="grid grid-cols-2 gap-4">
        <SearchableSelect
          label="ITI *"
          options={itiOptions}
          value={watchedIti}
          onChange={handleItiChange}
          disabled={modalDisabled.iti}
        />
        <SearchableSelect
          label="Trade *"
          options={tradeOptions}
          value={watchedTrade}
          onChange={handleTradeChange}
          disabled={modalDisabled.trade}
        />
      </div>
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Industry"
        actions={[{ label: "Add New", onClick: () => openModal(MODAL_MODE.ADD) }]}
        filters={filters}
        onResetFilters={onResetFilters}
        isLoading={listLoading}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: (p) => fetchList(p, pagination.limit),
          onLimitChange: (l) => fetchList(1, l),
        }}
      >
        <GenericTable
          columns={tableColumns}
          data={tableRows}
          pagination={pagination}
        />
      </GenericDataLayout>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalMode === MODAL_MODE.ADD ? "Add Industry" : "Edit Industry"}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {renderFormFields()}
          <div className={`mt-6 flex justify-end gap-3`}>
            <Button
              title={submitting ? "Processing..." : "Save"}
              disabled={submitting}
              type="submit"
            />
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default Industry;